/**
 * frontend/src/components/VoucherCard.jsx
 * Voucher teaser card.
 *
 * [Sprint UI-6] Deal-site anatomy, sizes taken from groupon.com mobile
 * (measured 2026-09-16): 16:9 photo with badge + favourite on top, then
 * merchant 14px, title 16/22 bold on two lines, address + distance 14px,
 * rating row, price row (old price struck, new price 18px green,
 * discount chip red). The card is fluid — the parent decides its width
 * (283px in a lane, a grid cell, or full width in a list).
 *
 * Ratings: every offer shows 5 stars and "5.0 (ახალი)" until real
 * reviews exist — a placeholder by decision, not data.
 *
 * Full info lives in VoucherDetailPage — opened on card click.
 */
import React from 'react';
import {
  MapPin,
  Ticket,
  Star,
  Utensils,
  Sparkles,
  Palmtree,
  Music,
  HeartPulse,
  Heart,
  Baby,
  Monitor,
  Pill,
  ShoppingCart,
  Car,
} from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_STYLE = {
  K: { bg: 'bg-orange-500',  Icon: Utensils },
  S: { bg: 'bg-pink-500',    Icon: Sparkles },
  D: { bg: 'bg-amber-500',   Icon: Palmtree },
  G: { bg: 'bg-violet-500',  Icon: Music },
  J: { bg: 'bg-blue-500',    Icon: HeartPulse },
  B: { bg: 'bg-emerald-500', Icon: Baby },
  T: { bg: 'bg-slate-700',   Icon: Monitor },
  A: { bg: 'bg-teal-500',    Icon: Pill },
  M: { bg: 'bg-green-600',   Icon: ShoppingCart },
  V: { bg: 'bg-red-500',     Icon: Car },
};
const CAT_DEFAULT = { bg: 'bg-slate-500', Icon: Ticket };

// "პოპულარული" badge once this many vouchers have been generated.
const POPULAR_MIN = 3;

function resolveUrl(u) {
  if (!u) return null;
  return u.startsWith('http') ? u : `${API_URL}${u}`;
}

function formatPrice(p) {
  if (p == null || isNaN(p)) return null;
  const n = Number(p);
  return Number.isInteger(n) ? `${n}₾` : `${n.toFixed(2)}₾`;
}

export default function VoucherCard({ offer, onClick }) {
  const style = CAT_STYLE[offer.partner_category] || CAT_DEFAULT;
  const Icon = style.Icon;

  // Favorites use product_id (current shape) with legacy _id fallback.
  const favId = offer.product_id || offer._id;
  const { isSaved, toggle } = useFavorites();
  const saved = isSaved(favId);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    toggle({ ...offer, product_id: favId });
  };

  // Photo priority: product image → partner main photo → category colour.
  const photo = resolveUrl(offer.image_url) || resolveUrl(offer.partner_main_photo);

  const partnerName = offer.partner_name || 'პარტნიორი';
  const productName = offer.title || partnerName;
  const address = offer.partner_address || '';

  const discount = offer.discount_percent;
  const price    = Number(offer.price) || 0;
  const hasDiscount = discount > 0 && price > 0;
  const discounted  = hasDiscount ? price * (1 - discount / 100) : price;
  const popular = (offer.voucher_count || 0) >= POPULAR_MIN;

  const distanceLabel = offer.distance_km != null
    ? (offer.distance_km < 1 ? '<1 კმ' : `${offer.distance_km} კმ`)
    : null;

  return (
    <div
      onClick={() => onClick && onClick(offer)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onClick) { e.preventDefault(); onClick(offer); } }}
      className="w-full flex flex-col text-left cursor-pointer active:opacity-90"
      data-testid={`voucher-card-${offer._id}`}
    >
      {/* PHOTO */}
      <div className={`relative w-full aspect-video rounded-xl overflow-hidden ${photo ? 'bg-slate-200' : style.bg}`}>
        {photo ? (
          <img
            src={photo}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="w-12 h-12 text-white/40" strokeWidth={1.4} />
          </div>
        )}

        {popular && (
          <span className="absolute top-2 left-2 flex items-center gap-1.5 bg-white text-slate-900 text-[13px] font-bold leading-none pl-2.5 pr-3 py-[7px] rounded-full shadow-sm">
            <Ticket className="w-3.5 h-3.5" strokeWidth={2} />
            პოპულარული
          </span>
        )}

        <button
          onClick={handleFavoriteClick}
          aria-label={saved ? 'რჩეულებიდან წაშლა' : 'რჩეულებში დამატება'}
          className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-sm active:scale-90 transition-transform"
          data-testid={`voucher-fav-${offer._id}`}
        >
          <Heart
            className={`w-[18px] h-[18px] ${saved ? 'fill-rose-500 text-rose-500' : 'text-slate-900'}`}
            strokeWidth={1.8}
          />
        </button>
      </div>

      {/* TEXT */}
      <div className="mt-2 text-[14px] leading-5 text-slate-800 truncate">{partnerName}</div>
      <div className="text-[16px] leading-[22px] font-bold text-slate-900 line-clamp-2">{productName}</div>

      {(address || distanceLabel) && (
        <div className="flex items-center gap-1.5 text-[14px] leading-5 text-slate-700 min-w-0">
          {address && <span className="truncate">{address}</span>}
          {distanceLabel && (
            <span className="flex items-center gap-0.5 text-slate-500 shrink-0">
              <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
              {distanceLabel}
            </span>
          )}
        </div>
      )}

      {/* Rating — placeholder until real reviews exist */}
      <div className="flex items-center gap-1.5 leading-5" aria-label="შეფასება 5.0, ახალი">
        <span className="flex gap-px">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" strokeWidth={0} />
          ))}
        </span>
        <span className="text-[14px] font-extrabold text-slate-900">5.0</span>
        <span className="text-[14px] font-semibold text-slate-500">(ახალი)</span>
      </div>

      {price > 0 && (
        <div className="flex items-baseline gap-2 mt-0.5">
          {hasDiscount && (
            <span className="text-[14px] text-slate-500 line-through">{formatPrice(price)}</span>
          )}
          <span className="text-[18px] leading-[22px] font-extrabold text-green-700">
            {formatPrice(hasDiscount ? discounted : price)}
          </span>
          {hasDiscount && (
            <span className="text-[13px] font-bold text-red-700 bg-red-50 rounded-md px-1.5 py-px">
              -{Math.round(discount)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
